import React from "react";
import { useCustom } from "@refinedev/core";
import { Alert, Button, Card, Col, Progress, Row, Skeleton, Statistic } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import type {
  CampaignResponse,
  CampaignStatsResponse,
} from "../../../api-client";

export interface CampaignStatsCardProps {
  campaignId: string;
  refreshKey: number;
  status: CampaignResponse["status"];
}

export const CampaignStatsCard: React.FC<CampaignStatsCardProps> = ({
  campaignId,
  refreshKey,
  status,
}) => {
  const {
    data: statsResponse,
    isLoading,
    isError,
    refetch,
    isRefetching,
  } = useCustom<CampaignStatsResponse>({
    url: `/api/campaigns/${campaignId}/stats`,
    method: "get",
    queryOptions: {
      queryKey: [campaignId, refreshKey],
      enabled: status !== "draft",
    },
  });

  const content = (() => {
    if (status === "draft") {
      return (
        <Alert
          type="info"
          showIcon
          message="Нет статистики - кампания еще не активирована"
        />
      );
    }

    if (isLoading) {
      return (
        <Row gutter={[16, 16]}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Col key={index} xs={12} sm={12} md={6} lg={6}>
              <Skeleton.Avatar
                active
                shape="square"
                style={{ width: "100%", height: 80, borderRadius: 8 }}
              />
            </Col>
          ))}
        </Row>
      );
    }

    if (isError) {
      return (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message="Не удалось загрузить статистику кампании"
          description="Проверьте подключение или повторите попытку."
          action={
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isRefetching}
            >
              Повторить
            </Button>
          }
        />
      );
    }

    const stats = statsResponse?.data;
    const totalTasks = stats?.total ?? 0;
    const successRate = stats?.success_rate ?? 0;

    const successRateColor = (() => {
      if (!totalTasks) {
        return "#d9d9d9";
      }

      if (successRate >= 80) {
        return "#52c41a";
      }

      if (successRate >= 50) {
        return "#faad14";
      }

      return "#f5222d";
    })();

    return (
      <div data-campaign-id={campaignId} data-refresh-key={refreshKey}>
        <span data-testid={`campaign-stats-${campaignId}`} style={{ display: "none" }}>
          {JSON.stringify(statsResponse ?? null)}
        </span>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Statistic title="Всего задач" value={stats?.total ?? 0} />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Statistic title="В ожидании" value={stats?.pending ?? 0} />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Statistic title="В процессе" value={stats?.processing ?? 0} />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Statistic
              title={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <CheckCircleOutlined style={{ color: "#52c41a" }} /> Успешно
                </span>
              }
              value={stats?.done ?? 0}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Statistic
              title={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <CloseCircleOutlined style={{ color: "#f5222d" }} /> Ошибки
                </span>
              }
              value={stats?.failed ?? 0}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={6}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
              <span>Успешность</span>
              <Progress
                type="circle"
                percent={successRate}
                strokeColor={successRateColor}
                format={() => (totalTasks === 0 ? "N/A" : `${Math.round(successRate)}%`)}
              />
            </div>
          </Col>
        </Row>
      </div>
    );
  })();

  return (
    <Card
      title="Статистика"
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => refetch()}
          loading={isRefetching}
          aria-label="Обновить статистику"
        >
          Обновить
        </Button>
      }
    >
      {content}
    </Card>
  );
};
